import { Typography } from '@mui/material';
import downloadCertificationPdf from 'helpers/investor/downloadCertificationPdf';
import useInvestorCertification from 'hooks/data/investor/useInvestorCertification';
import CurrentCertificationResult from './CurrentCertificationResult';
import ExpiredCertificationResult from './ExpiredCertificationResult';
import RecordRow from 'components/RecordRow';
import { Column, Row } from 'components/Layout';
import InvestorTypeSelector from 'components/InvestorTypeSelector';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';

type Props = {
  investorDetail: {
    _id: string;
    fullName?: string;
  };
};

const InvestorCertification: React.FC<Props> = ({ investorDetail }) => {
  const {
    certification,
    deleteCertification,
    resetCertification,
    manuallyPassCertification,
  } = useInvestorCertification(investorDetail._id);
  const [canEdit] = useHasPermissions([RolePermission.EditInvestor]);

  const investorCertification = certification?.data ?? [];

  const currentCertifications = investorCertification.filter(
    ({ forceExpired }) => !forceExpired,
  );
  const expiredCertifications = investorCertification.filter(
    ({ forceExpired }) => forceExpired,
  );

  return (
    <>
      <RecordRow
        label="Investor self-certification"
        direction={investorCertification.length ? 'column' : 'row'}
      >
        {!investorCertification.length ? (
          <>
            <span>N/A</span>
            {canEdit && (
              <InvestorTypeSelector
                loading={manuallyPassCertification.isPending}
                onClick={(investorType) =>
                  manuallyPassCertification.mutate(investorType)
                }
              />
            )}
          </>
        ) : (
          <Column spacing="xs">
            {!currentCertifications.length &&
              !!expiredCertifications.length && (
                <Row spacing="xs">
                  <span>Re-certification requested</span>
                  {canEdit && (
                    <InvestorTypeSelector
                      loading={manuallyPassCertification.isPending}
                      onClick={(investorType) =>
                        manuallyPassCertification.mutate(investorType)
                      }
                    />
                  )}
                </Row>
              )}
            {currentCertifications.map((certification) => (
              <CurrentCertificationResult
                key={certification._id}
                certification={certification}
                isResetting={resetCertification.isPending}
                onReset={() => resetCertification.mutate()}
                onDownload={(certification) =>
                  downloadCertificationPdf(
                    investorDetail.fullName ?? '',
                    certification,
                  )
                }
              />
            ))}
            {!!expiredCertifications.length && (
              <Typography component="i" variant="caption">
                Previous results
              </Typography>
            )}
            {expiredCertifications.map((certification) => (
              <ExpiredCertificationResult
                key={certification._id}
                certification={certification}
                isDeleting={deleteCertification.isPending}
                onDelete={(certificationId) =>
                  deleteCertification.mutate(certificationId)
                }
                onDownload={(certification) =>
                  downloadCertificationPdf(
                    investorDetail.fullName ?? '',
                    certification,
                  )
                }
              />
            ))}
          </Column>
        )}
      </RecordRow>
    </>
  );
};

export default InvestorCertification;
